// « Mes partenaires » (conception du 29/10/2026, § 6) : les partenaires extérieurs liés à mon organisation, avec les
// fiches partagées et les affaires transmises à chacun, l'invitation d'un partenaire et, pour l'administrateur de
// l'organisation, la fin d'un partenariat. Les droits réels sont ceux de la base (`end_partnership`).
import { ChevronRight, UserPlus } from "lucide-react";
import { useGetList, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  Deal,
  PartnerOrg,
  Partnership,
  ProjectShare,
  Sale,
} from "../types";
import { InvitePartnerDialog } from "./InvitePartnerDialog";
import { canHostPartners, type PartnerRow, partnerRows } from "./partnerships";
import { usePartnerOrg } from "./usePartnerOrg";

const all = { page: 1, perPage: 500 };

export const PartnersPage = () => {
  const translate = useTranslate();
  const { sale, isPending } = usePartnerOrg();
  if (isPending) return null;
  // Un invité qui tape l'adresse.
  if (!canHostPartners(sale) || sale?.org_id == null)
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        {translate("resources.partnerships.mine.not_available")}
      </p>
    );
  return <PartnersContent myOrgId={sale.org_id} orgAdmin={!!sale.org_admin} />;
};

const PartnersContent = ({
  myOrgId,
  orgAdmin,
}: {
  myOrgId: number;
  orgAdmin: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const [inviting, setInviting] = useState(false);
  const [ending, setEnding] = useState<PartnerRow | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: partnerships, refetch: refetchPartnerships } =
    useGetList<Partnership>("partnerships", {
      filter: { host_org_id: myOrgId, "ended_at@is": null },
      pagination: all,
      sort: { field: "id", order: "ASC" },
    });
  const { data: shares, refetch: refetchShares } = useGetList<ProjectShare>(
    "project_shares",
    {
      filter: { host_org_id: myOrgId, "revoked_at@is": null },
      pagination: all,
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: transmittedDeals, refetch: refetchDeals } = useGetList<Deal>(
    "deals",
    {
      filter: {
        org_id: myOrgId,
        "transmitted_by@not.is": null,
        "archived_at@is": null,
      },
      pagination: all,
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: orgs, refetch: refetchOrgs } = useGetList<PartnerOrg>(
    "partner_orgs",
    {
      pagination: all,
      sort: { field: "id", order: "ASC" },
    },
  );
  // Affaires confiées avant l'étape 2, sans transmitted_by : celles que tient directement un partenaire.
  const partnerIds = (partnerships ?? []).map((p) => p.partner_sale_id);
  const { data: legacyDeals, refetch: refetchLegacyDeals } = useGetList<Deal>(
    "deals",
    {
      filter: {
        org_id: myOrgId,
        "transmitted_by@is": null,
        "archived_at@is": null,
        "sales_id@in": `(${partnerIds.join(",")})`,
      },
      pagination: all,
      sort: { field: "id", order: "ASC" },
    },
    { enabled: partnerIds.length > 0 },
  );
  const deals = transmittedDeals && [
    ...transmittedDeals,
    ...(legacyDeals ?? []),
  ];
  // Partenaires et titulaires des affaires transmises (un collègue du partenaire compte pour son organisation).
  const ids = [
    ...new Set([
      ...(partnerships ?? []).map((p) => p.partner_sale_id),
      ...(deals ?? []).map((d) => Number(d.sales_id)),
    ]),
  ].filter((id) => Number.isFinite(id) && id > 0);
  const { data: sales, refetch: refetchSales } = useGetList<Sale>(
    "sales",
    {
      filter: { "id@in": `(${ids.join(",")})` },
      pagination: all,
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: ids.length > 0 },
  );
  if (!partnerships || !shares || !deals || !orgs) return null;

  const rows = partnerRows({
    myOrgId,
    partnerships,
    sales: sales ?? [],
    orgs,
    shares,
    deals,
  });

  const refresh = () => {
    refetchPartnerships();
    refetchShares();
    refetchDeals();
    if (partnerIds.length > 0) refetchLegacyDeals();
    refetchSales();
    // Un nouveau partenaire amène son organisation, illisible jusque-là.
    refetchOrgs();
  };

  const end = async () => {
    if (!ending) return;
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("end_partnership", {
      link: ending.partnershipId,
    });
    setBusy(false);
    refresh();
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.partnerships.mine.ended", {
      type: "info",
      messageArgs: { name: ending.name },
    });
    setEnding(null);
  };

  const date = (value: string) => new Date(value).toLocaleDateString();
  const offer = (row: PartnerRow) =>
    translate(
      row.subscriber
        ? "resources.partnerships.mine.subscriber"
        : "resources.partnerships.mine.invited",
    );
  const endButton = (row: PartnerRow) =>
    orgAdmin && (
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 md:min-h-8"
        onClick={() => setEnding(row)}
      >
        {translate("resources.partnerships.mine.end")}
      </Button>
    );

  return (
    <div className="flex flex-col gap-4 mt-4 max-w-4xl">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-semibold">
            {translate("resources.partnerships.mine.title")}
          </h1>
          <Button
            size="sm"
            className="min-h-11 md:min-h-8"
            onClick={() => setInviting(true)}
          >
            <UserPlus className="size-4" aria-hidden />
            {translate("resources.partnerships.mine.invite")}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          {translate("resources.partnerships.mine.intro")}
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.partnerships.mine.empty")}
        </p>
      ) : isMobile ? (
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <Card key={row.partnershipId}>
              <CardContent className="flex flex-col gap-2 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium break-words min-w-0">
                    {row.name}
                  </span>
                  <Badge variant="outline">{offer(row)}</Badge>
                </div>
                {row.orgName && (
                  <span className="text-muted-foreground break-words">
                    {row.orgName}
                  </span>
                )}
                <Line
                  label={translate("resources.partnerships.mine.shares")}
                  value={row.shares}
                />
                <Line
                  label={translate("resources.partnerships.mine.deals")}
                  value={row.deals}
                />
                <Line
                  label={translate("resources.partnerships.mine.since")}
                  value={date(row.since)}
                />
                {orgAdmin && <div className="pt-1">{endButton(row)}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {translate("resources.partnerships.mine.name")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.organisation")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.offer")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.shares")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.deals")}
              </TableHead>
              <TableHead>
                {translate("resources.partnerships.mine.since")}
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.partnershipId}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{row.orgName ?? ""}</TableCell>
                <TableCell>{offer(row)}</TableCell>
                <TableCell>{row.shares}</TableCell>
                <TableCell>{row.deals}</TableCell>
                <TableCell>{date(row.since)}</TableCell>
                <TableCell className="text-right">{endButton(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <InvitePartnerDialog
        open={inviting}
        onOpenChange={setInviting}
        onInvited={async () => {
          refresh();
          return true;
        }}
      />
      <Dialog open={!!ending} onOpenChange={(open) => !open && setEnding(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate("resources.partnerships.mine.end_title", {
                name: ending?.name ?? "",
              })}
            </DialogTitle>
            <DialogDescription>
              {translate("resources.partnerships.mine.end_confirm")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setEnding(null)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button variant="destructive" disabled={busy} onClick={end}>
              {translate("resources.partnerships.mine.end")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Line = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex justify-between gap-2">
    <span className="text-muted-foreground">{label}</span>
    <span>{value}</span>
  </div>
);

PartnersPage.path = "/partenaires";

/** Entrée de « Mes partenaires » dans les paramètres du téléphone (même rendu que « Mon organisation »). */
export const PartnersSection = () => {
  const translate = useTranslate();
  const { sale } = usePartnerOrg();
  if (!canHostPartners(sale)) return null;
  return (
    <ItemGroup className="rounded-lg border overflow-hidden">
      <Item asChild size="sm" className="cursor-pointer">
        <Link to={PartnersPage.path}>
          <ItemContent>
            <ItemTitle className="font-normal">
              {translate("resources.partnerships.mine.title")}
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            <ChevronRight className="size-4 text-muted-foreground" />
          </ItemActions>
        </Link>
      </Item>
    </ItemGroup>
  );
};
